import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import useRole from '../hooks/useRole'
import { useSchoolId } from '../lib/auth/schoolContext'
import { Icons } from '../lib/Icons'
import { db, auth } from '../firebase'

import QuickActions from '../components/dashboard/QuickActions'
import ReviewsDue from '../components/dashboard/ReviewsDue'
import RecentActivity from '../components/dashboard/RecentActivity'
import Announcements from '../components/dashboard/Announcements'
import DashboardStats from '../components/dashboard/DashboardStats'
import InterventionGroupReminders from '../components/dashboard/InterventionGroupReminders'
import SencoProvisionAlerts from '../components/dashboard/SencoProvisionAlerts'
import SencoCommandCenter from '../components/dashboard/SencoCommandCenter'
import SchoolPageHeader from '../components/school/SchoolPageHeader'
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../lib/auth/capabilities'
import { useStudents } from '../lib/useStudents'
import { resolveStudentSearch } from '../lib/studentSearch'

export default function Dashboard() {
  const navigate = useNavigate()
  const { role, isSENCO } = useRole()
  const [searchQuery, setSearchQuery] = useState('')
  const searchRef = useRef(null)
  const { schoolId } = useSchoolId()
  const { can } = useSchoolCapabilities()
  const { students } = useStudents({ orderByLastName: true, ensureSubscription: true })

  // Keyboard shortcut to focus search (Cmd/Ctrl+K or '/')
  useEffect(() => {
    const onKey = (e) => {
      const isModK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
      const isSlash = !e.metaKey && !e.ctrlKey && e.key === '/'
      if (isModK || isSlash) {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const roleLabel = useMemo(() => role || 'Member', [role])
  
  const user = useMemo(() => {
    const currentUser = auth.currentUser
    let displayName = 'User'
    
    if (currentUser?.displayName) {
      displayName = currentUser.displayName
    } else if (currentUser?.email) {
      // Extract name from email (e.g., "john.doe@school.com" -> "John Doe")
      const emailName = currentUser.email.split('@')[0]
      displayName = emailName
        .split(/[._-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }
    
    return {
      name: displayName,
      email: currentUser?.email || '',
      role: roleLabel,
    }
  }, [roleLabel])

  const handleSearch = (e) => {
    e.preventDefault()
    const queryText = searchQuery.trim()
    if (!queryText) return
    const match = resolveStudentSearch(students, queryText)
    if (match?.id) {
      navigate(`/student/${match.id}`)
      setSearchQuery('')
      return
    }
    navigate(`/student-organization?search=${encodeURIComponent(queryText)}`)
  }

  return (
    <AppLayout>
      <div className="min-h-full -mx-4 sm:-mx-6 lg:-mx-8 -mt-6">
        <header className="bg-slate-100 pb-24 px-4 sm:px-6 lg:px-8">
          <div className="relative py-6 space-y-4">
            <SchoolPageHeader
              title={schoolId ? `${schoolId.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} Command Centre` : 'SEN-Ed Schools'}
              description="Pilot-critical workflows are prioritized below so SENCOs can move from student discovery to evidence-backed decisions quickly."
              icon={Icons.Home}
              actions={[
                can(SCHOOL_ACTIONS.VIEW_STUDENTS) ? { label: 'Open Students', href: '/student-organization' } : null,
                can(SCHOOL_ACTIONS.GENERATE_EHCNA) ? { label: 'EHCNA Requests', href: '/ehcna', variant: 'secondary' } : null,
              ].filter(Boolean)}
            />
            <form onSubmit={handleSearch} className="mx-auto w-full max-w-xl">
              <label htmlFor="search" className="sr-only">Find a student</label>
              <div className="relative">
                <input
                  id="search"
                  ref={searchRef}
                  name="search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Find a student by name, year group, or need"
                  aria-label="Find a student"
                  className="peer block w-full rounded-xl border border-slate-300 bg-white py-2.5 pr-3 pl-10 text-base text-slate-900 outline-hidden placeholder:text-slate-500 focus:border-indigo-500 sm:text-sm transition-all"
                />
                <Icons.MagnifyingGlass
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400"
                />
              </div>
            </form>
          </div>
        </header>

        <main className="-mt-24 pb-8 px-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="sr-only">Dashboard</h1>
            
            {/* Main 3 column grid */}
            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 lg:gap-8">
              {/* Left column - 2/3 width */}
              <div className="grid grid-cols-1 gap-4 lg:col-span-2">
                {/* Welcome panel */}
                <section aria-labelledby="profile-overview-title">
                  <div className="overflow-hidden rounded-2xl bg-white shadow-md border border-gray-100">
                    <h2 id="profile-overview-title" className="sr-only">
                      Profile Overview
                    </h2>
                    <div className="bg-white p-6">
                      <div className="sm:flex sm:items-center sm:justify-between">
                        <div className="sm:flex sm:items-center sm:space-x-4">
                          <div className="hidden sm:block p-3 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl">
                            <Icons.User className="h-8 w-8 text-blue-600" />
                          </div>
                          <div className="mt-4 text-center sm:mt-0 sm:text-left">
                            <p className="text-sm font-medium text-gray-500">Welcome back,</p>
                            <p className="text-xl font-bold text-gray-900 sm:text-2xl">{user.name}</p>
                            <p className="text-sm font-medium text-blue-600">{user.role}</p>
                          </div>
                        </div>
                        <div className="mt-5 flex justify-center sm:mt-0">
                          <button
                            onClick={() => navigate('/student-organization')}
                            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2.5 text-sm font-medium text-white shadow-md hover:from-blue-700 hover:to-blue-600 hover:shadow-lg transition-all"
                          >
                            <Icons.Users className="h-4 w-4" />
                            View all students
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <DashboardStats />
                  </div>
                </section>

                {/* Quick Actions panel */}
                <section aria-labelledby="quick-links-title">
                  <h2 id="quick-links-title" className="sr-only">
                    Quick links
                  </h2>
                  <QuickActions />
                </section>

                {/* SENCO provision & waitlist alerts */}
                {isSENCO && (
                  <section aria-labelledby="senco-provision-alerts-title">
                    <h2 id="senco-provision-alerts-title" className="sr-only">
                      Provision and waitlist alerts
                    </h2>
                    <SencoProvisionAlerts />
                  </section>
                )}

                {isSENCO && (
                  <section aria-labelledby="senco-cohort-link-title">
                    <h2 id="senco-cohort-link-title" className="sr-only">
                      Cohort data
                    </h2>
                    <div className="rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-white p-5 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Cohort Data</h3>
                        <p className="mt-1 text-sm text-gray-600">
                          EHCP counts, at-expected percentages by year group, and inspection-style drill-downs.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/cohort-data')}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
                      >
                        <Icons.ChartBar className="h-4 w-4" />
                        Open Cohort Data
                      </button>
                    </div>
                  </section>
                )}

                {/* SENCO command center */}
                {isSENCO && (
                  <section aria-labelledby="senco-command-center-title">
                    <h2 id="senco-command-center-title" className="sr-only">
                      SENCO command center
                    </h2>
                    <SencoCommandCenter />
                  </section>
                )}
              </div>

              {/* Right column - 1/3 width */}
              <div className="grid grid-cols-1 gap-4">
                {/* Reviews / Annual Reviews */}
                <section aria-labelledby="reviews-title">
                  <h2 id="reviews-title" className="sr-only">
                    {isSENCO ? 'Annual Reviews' : 'Reviews Due'}
                  </h2>
                  <ReviewsDue />
                </section>

                {/* Intervention Group Reminders (for staff leading groups) */}
                <section aria-labelledby="intervention-reminders-title">
                  <h2 id="intervention-reminders-title" className="sr-only">
                    Intervention Group Reminders
                  </h2>
                  <InterventionGroupReminders />
                </section>

                {/* Announcements */}
                <section aria-labelledby="announcements-title">
                  <h2 id="announcements-title" className="sr-only">
                    Announcements
                  </h2>
                  <Announcements />
                </section>
              </div>
            </div>

            {/* Recent Activity - Full Width */}
            <div className="mt-8">
              <RecentActivity />
            </div>
          </div>
        </main>
      </div>
    </AppLayout>
  )
}
